import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import API_BASE_URL from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";
import axios from "axios";
import { FaBook, FaUserTie, FaUsers, FaTasks, FaDownload, FaChartLine, FaCheckCircle, FaExternalLinkAlt } from "react-icons/fa";

function GuideHomeContainer() {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [userName, setUserName] = useState("Guide");
    const [department, setDepartment] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!isAuthenticated || !user) {
                    console.error("User not authenticated");
                    navigate("/");
                    return;
                }

                setUserName(user.name || "Guide");
                setDepartment(user.department || "Department");

                const response = await axios.get(
                    `${API_BASE_URL}/api/projects/guide/assigned/${user.email}`
                );

                if (response.data.success) {
                    setProjects(response.data.data || []);
                }

                const announcementsResponse = await axios.get(
                    `${API_BASE_URL}/api/projects/announcements/general`
                );

                if (announcementsResponse.status === 200) {
                    setAnnouncements(
                        announcementsResponse.data.data ||
                        announcementsResponse.data ||
                        []
                    );
                }
            } catch (error) {
                console.error("Error loading data:", error);
                if (error.response?.status === 401) {
                    navigate("/");
                }
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated) {
            fetchData();
            AOS.init({ duration: 1000 });
        }
    }, [isAuthenticated, user, navigate]);

    const gotoProgress = () => {
        navigate("/guide/progress");
    };

    const viewProject = (projectId) => {
        navigate(`/project/${projectId}`);
    };

    const truncateText = (text, maxLength) => {
        if (!text) return "";
        return text.length > maxLength
            ? text.substring(0, maxLength) + "..."
            : text;
    };

    return (
        <div className="min-h-screen p-4 md:p-8 font-sans">
            <div className="max-w-7xl mx-auto space-y-8">
                {/* Header Section */}
                <div
                    className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
                    data-aos="fade-down"
                >
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
                            <FaUserTie className="text-[#B7202E]" /> Welcome, {userName}
                        </h1>
                        <p className="text-gray-500 mt-2">Manage your assigned projects and track student progress.</p>
                    </div>
                    <button
                        className="bg-[#B7202E] text-white px-6 py-3 rounded-xl font-bold shadow-sm hover:bg-[#961925] hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center gap-2"
                        onClick={gotoProgress}
                    >
                        <FaChartLine /> Track Progress
                    </button>
                </div>

                {/* Projects Grid */}
                <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <FaTasks className="text-[#B7202E]" /> Your Assigned Projects
                    </h2>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center min-h-[40vh]">
                            <div className="w-12 h-12 border-4 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mb-4"></div>
                            <p className="text-gray-500 font-medium">Loading projects...</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {projects && projects.length > 0 ? (
                                projects.map((project, index) => (
                                    <div
                                        key={project._id || index}
                                        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-300 flex flex-col h-full group"
                                        data-aos="fade-up"
                                        data-aos-delay={index * 100}
                                    >
                                        <div className={`h-1.5 w-full ${project.guide?.assigned?.email === user.email
                                            ? "bg-green-500"
                                            : project.coGuides?.assigned?.email === user.email
                                                ? "bg-blue-500"
                                                : "bg-[#B7202E]"
                                            }`}></div>

                                        <div className="p-6 flex-1 flex flex-col gap-4">
                                            <div className="flex justify-between items-start gap-4">
                                                <h3 className="text-lg font-bold text-gray-800 leading-tight group-hover:text-[#B7202E] transition-colors">
                                                    {truncateText(project.projectTitle, 60)}
                                                </h3>
                                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide border whitespace-nowrap ${project.guide?.assigned?.email === user.email
                                                    ? "bg-green-50 text-green-700 border-green-200"
                                                    : project.coGuides?.assigned?.email === user.email
                                                        ? "bg-blue-50 text-blue-700 border-blue-200"
                                                        : "bg-gray-50 text-gray-600 border-gray-200"
                                                    }`}>
                                                    {project.guide?.assigned?.email === user.email ? "Main Guide" : "Co-Guide"}
                                                </span>
                                            </div>

                                            <div className="space-y-3 pt-2">
                                                <div className="flex items-start gap-2 text-sm text-gray-600">
                                                    <FaUsers className="mt-1 text-gray-400" />
                                                    <div>
                                                        <span className="font-semibold text-gray-700 block text-xs uppercase tracking-wider mb-1">Team:</span>
                                                        <div className="flex flex-wrap gap-1">
                                                            {project.teamMembers?.map((member, idx) => (
                                                                <span key={idx} className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                                                                    {member}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-start gap-2 text-sm text-gray-600">
                                                    <FaBook className="mt-1 text-gray-400" />
                                                    <div>
                                                        <span className="font-semibold text-gray-700 block text-xs uppercase tracking-wider mb-1">Domain:</span>
                                                        <span>{project.domain?.preference1?.name || "Not specified"}</span>
                                                    </div>
                                                </div>

                                                <div className="flex items-start gap-2 text-sm text-gray-600">
                                                    <FaCheckCircle className="mt-1 text-gray-400" />
                                                    <div>
                                                        <span className="font-semibold text-gray-700 block text-xs uppercase tracking-wider mb-1">Status:</span>
                                                        <span className="capitalize">{project.status?.replace("-", " ") || "Pending"}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-auto pt-4 border-t border-gray-50">
                                                <button
                                                    onClick={() => viewProject(project._id || index)}
                                                    className="w-full bg-[#506eec] hover:bg-[#3a5bbf] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                                                >
                                                    View Details <FaExternalLinkAlt className="text-xs" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-span-full bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
                                    <FaTasks className="mx-auto text-4xl text-gray-300 mb-4" />
                                    <h3 className="text-lg font-bold text-gray-800 mb-1">No Projects Assigned</h3>
                                    <p className="text-gray-500">You haven't been assigned to any projects yet.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default GuideHomeContainer;
